import usePartnerScoreDataAvailability from '@/api/hcp/partnerscore/usePartnerScoreDataAvailability';
import usePartnerScoreDataAvailabilityCompetitor from '@/api/hcp/partnerscore/usePartnerScoreDataAvailabilityCompetitor';
import { useGetUserCountries } from '@/api/queries/country/useGetUserCountries';
import LoadingSpinner from '@/storybook/components/LoadingSpinner/LoadingSpinner';
import { type SectionTitleButtonProps } from '@/storybook/components/SectionTitle/SectionTitle';
import WidgetCard from '@/storybook/components/WidgetCard/WidgetCard';
import { ChevronUpIcon } from '@/storybook/icons/ChevronDownIcon';
import classNames from 'classnames';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NoDataComponent from './components/NoDataComponent/NoDataComponent';
import TableData from './components/TableData/TableData';
import TableHeader from './components/TableHeader/TableHeader';
import { SortDirection } from './components/TableHeader/utils/TableHeaderTypes';
import Tooltip from './components/ToolTip/Tooltip';
import styles from './PartnerScoreDataAvailability.module.css';
import { ScoreData } from './PartnerScoreDataAvailabilityTypes/PartnerScoreDataAvailabilitytypes';
import { testIds } from './testids';
import { filterPartnerScore } from './utils/filteredPartnerScore';
import { useDataAvailabilityColumns } from './utils/useDataAvailabilityColumns';
import { getSortedUserCountries } from './utils/getSortedUserCountries';
import { handleHideClick, handleSort, hasDataForYear } from './utils/partnerScoreDataAvailabilityFunctions';
import { sortCountries } from './utils/sortUtils';
import { useDisplayedCountries } from './utils/useDisplayedCountries';
import useRecalculateTableWidth from './utils/useRecalculateTableWidth';
import { useUpdatePositions } from './utils/useUpdatePositions';
import { useUpdateTooltipPosition } from './utils/useUpdateTooltipPosition';

interface PartnerScoreDataAvailabilityProps {
  allPartnerScoresCompetitor?: ScoreData[];
  customIsLoading?: boolean;
  headerTextCompetitor?: string;
  initialTableVisible?: boolean;
  largerArrow?: boolean;
  year: number;
  quarter: number;
  currentAggregatedData: 'partners' | 'competitors';
}

const PartnerScoreDataAvailability = forwardRef<HTMLDivElement, PartnerScoreDataAvailabilityProps>((props, ref) => {
  const {
    allPartnerScoresCompetitor: propAllPartnerScoresCompetitor,
    customIsLoading: propCustomIsLoading,
    headerTextCompetitor: propHeaderTextCompetitor,
    initialTableVisible: propInitialTableVisible,
    largerArrow: propLargerArrow,
    currentAggregatedData,
    year,
    quarter,
  } = props;

  const initialTableVisible = propInitialTableVisible !== undefined ? propInitialTableVisible : true;
  const largerArrow = propLargerArrow !== undefined ? propLargerArrow : false;

  const { t } = useTranslation();

  const { data: competitorData, isLoading: isCompetitorLoading } = usePartnerScoreDataAvailabilityCompetitor();
  const { data: partnerData, isLoading: isPartnerLoading } = usePartnerScoreDataAvailability();

  const { data: userCountriesData = [], isLoading: isUserCountriesLoading } = useGetUserCountries();

  const selectedData = currentAggregatedData === 'partners' ? partnerData : competitorData;
  const selectedIsLoading = currentAggregatedData === 'partners' ? isPartnerLoading : isCompetitorLoading;

  const allPartnerScores = propAllPartnerScoresCompetitor || selectedData || [];
  const isLoading = propCustomIsLoading !== undefined ? propCustomIsLoading : selectedIsLoading;
  const headerTextCompetitor =
    propHeaderTextCompetitor || t('main.partnerScore.partnerScoreDataAvailability.widgetHeader');

  const [isTableVisible, setTableVisible] = useState<boolean>(initialTableVisible);
  const [selectedCountry] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: SortDirection }>({
    key: 'country',
    direction: SortDirection.Asc,
  });
  const [displayCount, setDisplayCount] = useState<number>(0);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const [yearPosition, setYearPosition] = useState({ top: 0, left: 0 });

  const yearRef = useRef(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const innerTableContainerRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => innerTableContainerRef.current, []);

  const { allYearQuarters, userCountries } = useDataAvailabilityColumns(allPartnerScores, userCountriesData);

  const filteredPartnerScore = filterPartnerScore(selectedCountry, allPartnerScores);

  const sortedUserCountries = getSortedUserCountries(userCountries, sortConfig.direction);
  const displayedCountries = useDisplayedCountries(
    userCountries,
    sortedUserCountries,
    filteredPartnerScore,
    displayCount,
  );

  const sortedDisplayedCountries = useMemo(
    () => sortCountries(displayedCountries, sortConfig),
    [displayedCountries, sortConfig],
  );

  const selectedCountries = useMemo(() => (selectedCountry ? [selectedCountry] : []), [selectedCountry]);

  useUpdatePositions(yearRef, innerTableContainerRef, setTooltipPosition, setYearPosition, year, isTableVisible);
  useRecalculateTableWidth(innerTableContainerRef, [selectedCountry, allYearQuarters]);
  useUpdateTooltipPosition(yearRef, innerTableContainerRef, setTooltipPosition, year);

  const checkForYearData = (currentYear: number, currentQuarter: number) => {
    return hasDataForYear(filteredPartnerScore, currentYear, currentQuarter);
  };
  const hideClickHandler = () => handleHideClick(isTableVisible, setTableVisible);
  const sortHandler = () => handleSort(setSortConfig);

  const isTooltipVisible =
    isTableVisible && !isLoading && !isUserCountriesLoading && filteredPartnerScore.length > 0 && !!year;

  useEffect(() => {
    const tooltipRoot = document.createElement('span');
    tooltipRoot.id = 'tooltip-root';
    document.body.appendChild(tooltipRoot);
  }, [isTableVisible]);

  useEffect(() => {
    setDisplayCount(5);
  }, [selectedCountry]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTooltipPosition((prevPosition: { top: number; left: number }) => ({
        ...prevPosition,
        left: year ? yearPosition.left : 0,
      }));
    }, 100);
    return () => clearTimeout(timer);
  }, [year, quarter, yearPosition]);

  const buttonProps: SectionTitleButtonProps = {
    disabled: !filteredPartnerScore.length,
    iconOnly: true,
    icon: <ChevronUpIcon />,
    variant: 'text',
    size: 'M',
    onClick: filteredPartnerScore.length > 0 ? hideClickHandler : undefined,
    className: classNames({
      [styles.buttonArrowUp]: isTableVisible,
      [styles.buttonArrowDown]: !isTableVisible,
      [styles.buttonLargerArrow]: largerArrow && !isTableVisible,
    }),
  };

  return (
    <WidgetCard
      title={headerTextCompetitor}
      heading="h3"
      button={buttonProps}
      displayContent={isTableVisible}
      data-testid={testIds.partnerScoreContainer}
      header-testid={testIds.partnerScoreTopWidget}
      title-testid={testIds.partnerScoreHeader}
      button-testid="dataAvailability-visbility-toggle"
      ctaContainerAdditionalStyles={styles.ctaContainerAdditionalStyles}
    >
      {isTableVisible && (
        <div
          ref={innerTableContainerRef}
          style={{ position: 'relative' }}
          data-testid={testIds.partnerScoreTableContainer}
        >
          {isLoading || isUserCountriesLoading ? (
            <LoadingSpinner hideLoadingMessage data-testid={testIds.partnerScoreLoadingSpinner} />
          ) : filteredPartnerScore.length === 0 ? (
            <NoDataComponent data-testid={testIds.partnerScoreNoData} />
          ) : (
            <div
              className={styles.partnerScoreDataAvailabilityContainerTable}
              data-testid={testIds.partnerScoreTableWrapper}
            >
              <table
                ref={tableRef}
                className={classNames(styles.fullWidthTable, {
                  [styles.fullWidth]: !selectedCountry,
                })}
                data-testid={testIds.partnerScoreTable}
              >
                <TableHeader
                  isScrolled={false}
                  handleSort={sortHandler}
                  sortConfig={sortConfig}
                  allYearQuarters={allYearQuarters}
                  selectedYear={year}
                  yearRef={yearRef}
                  selectedQuarter={quarter}
                  hasDataForYear={(currentYear: number) => checkForYearData(currentYear, year)}
                  data-testid={testIds.partnerScoreTableHeader}
                />
                <tbody data-testid={testIds.partnerScoreTableBody}>
                  {sortedDisplayedCountries.map((country, index) => (
                    <TableData
                      key={country.value}
                      country={country}
                      index={index}
                      filteredPartnerScore={filteredPartnerScore}
                      allYearQuarters={allYearQuarters}
                      selectedCountries={selectedCountries}
                      selectedYear={year}
                      selectedQuarter={quarter}
                      isLoading={false}
                      data-testid={testIds.partnerScoreTableRow(index)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Tooltip
            visible={isTooltipVisible}
            year={year}
            tooltipPosition={tooltipPosition}
            data-testid={testIds.partnerScoreTooltip}
            tableRef={tableRef}
          >
            <div>{t('main.partnerScore.partnerScoreDataAvailability.tooltipInfo')}</div>
          </Tooltip>
        </div>
      )}
      <div id="tooltip-root" />
    </WidgetCard>
  );
});

export default PartnerScoreDataAvailability;
